import { useCallback, useEffect, useState, type ReactNode } from "react";
import { useNavigate } from "react-router";
import {
  Alert,
  Badge,
  Button,
  Card,
  Input,
  Modal,
  Spinner,
  Table,
  type TableColumn,
} from "../../ds";
import {
  api,
  errorText,
  type DeviceDetail,
  type Firmware,
  type FirmwareProgress,
  type FlashSchedule,
} from "../../lib/api";
import { fmtBytes, fmtDateTime, nextNight, toLocalInput } from "../../lib/format";
import { useDeviceAction, type ActionNote } from "../../lib/hooks/useDeviceAction";
import { BackupTable } from "./BackupTable";
import { SystemSettingsCard } from "./SystemSettingsCard";

// Sektion „System“: Reboot, Backup, Firmware-Update. Alles Schreibaktionen →
// Variante A (Bestätigungsdialog), Firmware zusätzlich mit deutlicher Warnung.

/** Spalten des Katalogs im Flash-Dialog. Sechs statt sieben — mit der
 *  sha256-Spalte scrollte der Dialog auch bei voller Breite horizontal. */
function firmwareColumns(
  selected: Firmware | null,
  onPick: (f: Firmware) => void,
): TableColumn<Firmware>[] {
  return [
    {
      key: "version",
      header: "Version",
      // Tag direkt neben die Version — zwei Images derselben Version sind sonst
      // nur am sha256 zu unterscheiden, und genau dafür ist das Tag da (wie im
      // Katalog, components/images/FirmwareTable.tsx). Der sha256 hängt am
      // Tooltip: wer ihn wirklich vergleicht, ist im Katalog richtig.
      cell: (f) => (
        <span className="flex flex-wrap items-center gap-2" title={f.sha256}>
          {f.version}
          {f.tag && <Badge variant="info">{f.tag}</Badge>}
        </span>
      ),
    },
    { key: "model", header: "Modell", cell: (f) => f.model },
    {
      key: "size",
      header: "Größe",
      num: true,
      hideOnMobile: true,
      cell: (f) => fmtBytes(f.sizeBytes),
    },
    { key: "created", header: "Gebaut", hideOnMobile: true, cell: (f) => fmtDateTime(f.createdAt) },
    {
      key: "signed",
      header: "Signatur",
      // Ohne usign-Signatur lehnt der Router das Flashen ab — sichtbar machen,
      // bevor jemand ein Image wählt, das ohnehin nicht durchgeht.
      cell: (f) =>
        f.signed ? (
          <Badge variant="success" dot>
            signiert
          </Badge>
        ) : (
          <Badge variant="danger" dot title="Kein usign — der Router flasht das nicht">
            fehlt
          </Badge>
        ),
    },
    {
      key: "pick",
      header: "",
      align: "right",
      className: "actions",
      cell: (f) => (
        <Button
          size="sm"
          variant={selected?.id === f.id ? "primary" : "ghost"}
          iconName={selected?.id === f.id ? "check" : "upload"}
          disabled={!f.signed}
          title={f.signed ? undefined : "Ohne usign-Signatur nicht flashbar"}
          onClick={() => onPick(f)}
        >
          {selected?.id === f.id ? "gewählt" : "wählen"}
        </Button>
      ),
    },
  ];
}

/** Firmware-Katalog + Auswahl. Lädt erst beim Öffnen (Server-DB, kein Router). */
function FirmwareDialog({
  open,
  onClose,
  onFlash,
  onSchedule,
  planned,
  flashing,
}: {
  open: boolean;
  onClose: () => void;
  onFlash: (firmware: Firmware) => void;
  onSchedule: (firmware: Firmware, runAt: string) => void;
  /** Es gibt schon einen Termin — ein zweiter wäre ein Rennen um dieselbe
   *  Partition, der Server lehnt ihn ab. */
  planned: boolean;
  flashing: boolean;
}) {
  const [rows, setRows] = useState<Firmware[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Firmware | null>(null);
  const [planning, setPlanning] = useState(false);

  useEffect(() => {
    if (!open) return;
    api
      .listFirmware()
      .then((f) => {
        setRows(f);
        setError(null);
      })
      .catch((e: unknown) => {
        setRows([]);
        setError(errorText(e));
      });
  }, [open]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Firmware-Update"
      subtitle="Der Router lädt das Image selbst herunter, prüft die usign-Signatur und flasht."
      // xl statt lg: bei sechs Spalten scrollte der Dialog am Desktop
      // horizontal — eine Tabelle, die man erst wegschieben muss, liest niemand.
      size="xl"
      actions={
        <>
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            variant="outline"
            iconName="timer"
            disabled={!selected || planned}
            title={planned ? "Für diesen Router ist schon ein Update geplant" : undefined}
            onClick={() => setPlanning(true)}
          >
            Flash planen
          </Button>
          <Button
            variant="danger"
            iconName="upload"
            disabled={!selected}
            loading={flashing}
            onClick={() => selected && onFlash(selected)}
          >
            Jetzt flashen
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Alert variant="danger" title="Kein Rollback">
          Ein fehlgeschlagenes Update macht den Router aus der Ferne unbrauchbar — es gibt keine
          zweite Partition und kein automatisches Zurück. Nur Images einspielen, die zum Modell
          passen und vorher an einem Testgerät liefen.
        </Alert>
        <Alert variant="info" title="Backup läuft automatisch">
          Vor dem Flashen holt der Server ein Konfigurations-Backup und legt es im Katalog ab. Kommt
          es nicht an, wird nicht geflasht.
        </Alert>
        {error && <Alert variant="danger">{error}</Alert>}
        {rows === null ? (
          <div className="flex justify-center p-8">
            <Spinner />
          </div>
        ) : (
          <Table
            columns={firmwareColumns(selected, setSelected)}
            rows={rows}
            rowKey={(f) => f.id}
            emptyMessage="Kein Image im Katalog — erst unter „Images & Modelle“ bauen."
          />
        )}
      </div>
      {selected && planning && (
        <FlashPlanModal
          label={firmwareLabel(selected)}
          onClose={() => setPlanning(false)}
          onConfirm={(runAt) => {
            setPlanning(false);
            onSchedule(selected, runAt);
          }}
        />
      )}
    </Modal>
  );
}

/** „25.12.5 · eulektro" — dieselbe Benennung im Dialog wie in der Kachel. */
function firmwareLabel(f: { version: string; tag: string }): string {
  return f.tag ? `${f.version} · ${f.tag}` : f.version;
}

/** Termin für ein Update — zum Anlegen wie zum Verschieben. Ausgeführt wird es
 *  vom Server, nicht vom Browser: der Tab darf zu sein, der Rechner aus. */
// Der Dialog wird nur eingehängt, solange er offen ist — der Aufrufer schreibt
// `{offen && <FlashPlanModal …>}`. Damit ist jede Eingabe an eine Öffnung
// gebunden, statt sie beim nächsten Mal per Effekt zurücksetzen zu müssen: ein
// Dialog mit der Eingabe von vorletzter Woche plant Termine, die niemand meinte.
function FlashPlanModal({
  label,
  initial,
  onClose,
  onConfirm,
}: {
  /** Was geflasht werden soll — steht als Untertitel im Dialog. */
  label: string;
  /** Vorbelegung beim Verschieben (ISO). Ohne: nächste Nacht, 03:00. */
  initial?: string;
  onClose: () => void;
  onConfirm: (runAt: string) => void;
}) {
  const [value, setValue] = useState(() =>
    initial ? toLocalInput(new Date(initial)) : nextNight(),
  );
  // Die Uhr einmal beim Öffnen lesen statt in jedem Renderlauf. Wer den Dialog
  // stundenlang offen liegen lässt, bekommt seinen Termin vom Server abgelehnt
  // (`schedule._parse_run_at`: „runAt is in the past“) — die Prüfung hier spart
  // den Roundtrip, sie ist nicht die Schranke.
  const [openedAt] = useState(() => Date.now());

  const parsed = new Date(value);
  const valid = value !== "" && !Number.isNaN(parsed.getTime()) && parsed.getTime() > openedAt;

  return (
    <Modal
      open
      onClose={onClose}
      title={initial ? "Termin verschieben" : "Update planen"}
      subtitle={`${label} auf diesen Router`}
      size="sm"
      actions={
        <>
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            variant="primary"
            iconName="timer"
            disabled={!valid}
            // Mit Zone: der Server kennt sonst nur die Ziffern auf der Uhr.
            onClick={() => onConfirm(parsed.toISOString())}
          >
            Termin setzen
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Input
          type="datetime-local"
          label="Zeitpunkt"
          value={value}
          min={toLocalInput(new Date())}
          helperText="Ortszeit. Der Server flasht auf die Minute genau, sobald der Router online ist."
          onChange={(e) => setValue(e.target.value)}
        />
        <Alert variant="warning">
          Der Router ist danach einige Minuten offline und meldet sich mit der neuen Firmware neu
          an. Ist er zum Termin nicht erreichbar, versucht der Server es zwei Stunden lang weiter —
          danach gilt der Termin als gescheitert.
        </Alert>
      </div>
    </Modal>
  );
}

/** Der offene Termin, direkt in der Firmware-Kachel: verschieben oder absagen,
 *  ohne die Seite zu wechseln. */
function PlannedUpdate({
  entry,
  busy,
  onMove,
  onCancel,
}: {
  entry: FlashSchedule;
  busy: boolean;
  onMove: () => void;
  onCancel: () => void;
}) {
  const running = entry.status === "running";
  return (
    <div className="border-info-border bg-info-bg flex flex-col gap-2 rounded border p-3">
      <div className="text-sm">
        <div className="font-medium">
          {running ? "Läuft gerade" : `Geplant: ${fmtDateTime(entry.runAt)}`}
        </div>
        <div className="text-content-2">
          {firmwareLabel({ version: entry.firmwareVersion, tag: entry.firmwareTag })}
        </div>
      </div>
      {!running && (
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" iconName="timer" disabled={busy} onClick={onMove}>
            Verschieben
          </Button>
          <Button size="sm" variant="ghost" iconName="close" loading={busy} onClick={onCancel}>
            Absagen
          </Button>
        </div>
      )}
    </div>
  );
}

/** Die Firmware-Kachel: aktuelle Version, ein offener Termin und der Einstieg
 *  in den Katalog. */
function FirmwareCard({
  version,
  disabled,
  flashing,
  planned,
  scheduleBusy,
  onOpen,
  onMove,
  onCancel,
}: {
  version: string | null;
  disabled: boolean;
  flashing: boolean;
  planned: FlashSchedule | null;
  scheduleBusy: boolean;
  onOpen: () => void;
  onMove: () => void;
  onCancel: () => void;
}) {
  return (
    <ActionCard
      title="Firmware"
      description={`Spielt ein Image aus dem Katalog ein. Aktuell: ${version ?? "unbekannt"}.`}
    >
      <div className="flex flex-col gap-3">
        {planned && (
          <PlannedUpdate entry={planned} busy={scheduleBusy} onMove={onMove} onCancel={onCancel} />
        )}
        <Button
          variant="outline"
          iconName="upload"
          disabled={disabled}
          loading={flashing}
          onClick={onOpen}
        >
          Update wählen
        </Button>
      </div>
    </ActionCard>
  );
}

/** Eine Systemaktion als Kachel — Titel, Erklärung, Knopf. */
function ActionCard({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <Card title={title}>
      <p className="mb-3 text-sm text-content-2">{description}</p>
      {children}
    </Card>
  );
}

function RebootModal({
  open,
  name,
  busy,
  onClose,
  onConfirm,
}: {
  open: boolean;
  name: string;
  busy: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Router neu starten?"
      size="sm"
      actions={
        <>
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button variant="danger" iconName="power" loading={busy} onClick={onConfirm}>
            Neu starten
          </Button>
        </>
      }
    >
      <p className="text-sm text-content-2">
        {name} startet neu und ist für ein paar Minuten offline. Laufende Verbindungen im LAN
        brechen ab.
      </p>
    </Modal>
  );
}

/** Router aus der Flotte nehmen. Bewusst auch offline möglich: der häufigste
 *  Fall ist ein Gerät, das kaputt ist und nie wiederkommt. */
function DeleteDeviceCard({ device }: { device: DeviceDetail }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const name = device.hostname ?? device.id;

  async function remove() {
    setBusy(true);
    try {
      await api.deleteDevice(device.id);
      void navigate("/fleet");
    } catch (e) {
      setError(errorText(e));
      setBusy(false);
    }
  }

  return (
    <>
      <Card title="Router löschen">
        <div className="flex flex-col gap-3">
          <p className="text-sm text-content-2">
            Nimmt den Router aus der Flotte. Er kann sich danach nicht mehr verbinden, sein
            WireGuard-Peer wird ausgeschaltet. Backups, Mitschnitte und Audit bleiben erhalten.
          </p>
          {error && <Alert variant="danger">{error}</Alert>}
          <div>
            <Button variant="danger-outline" iconName="trash" onClick={() => setOpen(true)}>
              Router löschen
            </Button>
          </div>
        </div>
      </Card>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Router löschen?"
        size="sm"
        actions={
          <>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Abbrechen
            </Button>
            <Button variant="danger" iconName="trash" loading={busy} onClick={() => void remove()}>
              Löschen
            </Button>
          </>
        }
      >
        <p className="text-sm text-content-2">
          {name} wird aus Spyship entfernt. Auf dem Router selbst ändert sich nichts — um ihn wieder
          aufzunehmen, muss er neu enrollt werden.
        </p>
      </Modal>
    </>
  );
}

/** Was gerade läuft, samt Dauer.
 *
 *  Bewusst ohne Prozentzahl: der Server puffert die Antwort über Daphne und
 *  Caddy, wäre also sofort „fertig", während der Router über LTE noch lädt.
 *  Ein Balken, der nach zwei Sekunden auf 100 % steht und dann fünfzehn wartet,
 *  ist schlechter als gar keiner (core/firmware.py → note_download). */
function FirmwareProgressBar({
  progress,
  seconds,
}: {
  progress: FirmwareProgress | null;
  seconds: number | null;
}) {
  const backing = progress?.phase !== "download";
  return (
    <Alert variant="info">
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
          <span>
            {backing
              ? "Konfigurations-Backup wird geholt — danach lädt der Router das Image."
              : `Der Router lädt das Image${progress?.totalBytes ? ` (${fmtBytes(progress.totalBytes)})` : ""} und flasht anschließend selbst.`}
          </span>
          <span className="tabular-nums text-content-3">
            {seconds === null ? "…" : `seit ${seconds} s`}
          </span>
        </div>
        <div className="spy-progress">
          <span />
        </div>
      </div>
    </Alert>
  );
}

/** Termin-Zustand eines Routers: laden, planen, verschieben, absagen. Eigener
 *  Haken, damit die Sektion nicht vier weitere Zustände mit sich herumträgt. */
function useFlashSchedule(deviceId: string, setNote: (note: ActionNote) => void) {
  const [schedules, setSchedules] = useState<FlashSchedule[]>([]);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(() => {
    void api
      .listFlashSchedules(deviceId)
      .then(setSchedules)
      .catch(() => setSchedules([]));
  }, [deviceId]);
  // Einmal beim Öffnen. Kein Polling: der Termin steht in der Server-DB, nicht
  // auf dem Router — er ändert sich nur, wenn hier jemand etwas tut.
  useEffect(reload, [reload]);

  /** Ändern, Liste neu holen, Rückmeldung setzen — dreimal dasselbe. */
  const act = useCallback(
    async <T,>(action: () => Promise<T>, okText: (result: T) => string) => {
      setBusy(true);
      try {
        setNote({ variant: "success", text: okText(await action()) });
        reload();
      } catch (e) {
        setNote({ variant: "danger", text: errorText(e) });
      } finally {
        setBusy(false);
      }
    },
    [reload, setNote],
  );

  return {
    planned: schedules.find((s) => s.status === "pending" || s.status === "running") ?? null,
    busy,
    act,
  };
}

/** Phase des laufenden Updates, sekündlich beim Server geholt. Nur solange
 *  wirklich geflasht wird — der Agent antwortet erst danach, bis dahin ist die
 *  Server-Sicht („sichert" / „Router holt ab") das Einzige, was es zu zeigen
 *  gibt. */
function useFirmwareProgress(deviceId: string, active: boolean) {
  // Die Dauer wird hier gezählt, nicht im Renderlauf der Anzeige: die Uhr im
  // Render zu lesen macht die Komponente unrein (react-hooks/purity), und
  // gefragt wird der Server ohnehin jede Sekunde.
  const [state, setState] = useState<{ progress: FirmwareProgress; seconds: number | null } | null>(
    null,
  );
  useEffect(() => {
    if (!active) return;
    let alive = true;
    const tick = () => {
      void api
        .firmwareProgress(deviceId)
        .then(
          (p) =>
            alive &&
            setState({
              progress: p,
              seconds: p.since
                ? Math.max(0, Math.round((Date.now() - new Date(p.since).getTime()) / 1000))
                : null,
            }),
        )
        .catch(() => {});
    };
    tick();
    const timer = setInterval(tick, 1000);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, [active, deviceId]);
  // Solange nicht geflasht wird, gibt es keinen Fortschritt — der Stand vom
  // letzten Lauf darf nicht stehen bleiben. Ableiten statt im Effekt löschen.
  return active ? state : null;
}

export function SystemSection({ device }: { device: DeviceDetail }) {
  const { busy, note, setNote, run } = useDeviceAction(device.id);
  const [confirmReboot, setConfirmReboot] = useState(false);
  const [firmwareOpen, setFirmwareOpen] = useState(false);
  const flashProgress = useFirmwareProgress(device.id, busy === "firmware_update");
  // Zähler nur als React-`key` der Backup-Tabelle: nach einem erzeugten Backup
  // hängt die Tabelle neu und holt den Katalog frisch — ohne Reload-Brücke.
  const [backupRuns, setBackupRuns] = useState(0);

  const [moving, setMoving] = useState(false);
  const { planned, busy: scheduleBusy, act: actOnSchedule } = useFlashSchedule(device.id, setNote);

  async function reboot() {
    setConfirmReboot(false);
    await run("reboot", undefined, "Neustart ausgelöst — der Router ist ein paar Minuten weg.");
  }

  async function backup() {
    // Die Bytes gehen per HTTPS vom Router an den Server (wire-protocol-v1.md);
    // die WebUI lädt hier nichts herunter, sie stößt nur an.
    await run("backup", undefined, "Backup erstellt — es liegt jetzt im Katalog unten.");
    setBackupRuns((n) => n + 1);
  }

  async function flash(firmware: Firmware) {
    setFirmwareOpen(false);
    // Nur die Katalog-ID: URL, sha256 und usign-Signatur setzt der Server ein.
    await run(
      "firmware_update",
      { firmwareId: firmware.id },
      `Update auf ${firmware.version} gestartet — der Router meldet sich nach dem Flashen neu an.`,
    );
    // Der Server hat vorher gesichert — die Tabelle unten zeigt das Backup sonst
    // erst nach einem Reload.
    setBackupRuns((n) => n + 1);
  }

  async function planFlash(firmware: Firmware, runAt: string) {
    setFirmwareOpen(false);
    await actOnSchedule(
      () => api.scheduleFlash(device.id, firmware.id, runAt),
      (entry) => `Update auf ${firmware.version} geplant für ${fmtDateTime(entry.runAt)}.`,
    );
  }

  async function moveSchedule(runAt: string) {
    setMoving(false);
    if (!planned) return;
    await actOnSchedule(
      () => api.rescheduleFlash(planned.id, runAt),
      (entry) => `Termin verschoben auf ${fmtDateTime(entry.runAt)}.`,
    );
  }

  async function cancelSchedule() {
    if (!planned) return;
    await actOnSchedule(
      () => api.cancelFlashSchedule(planned.id),
      () => "Termin abgesagt.",
    );
  }

  const disabled = !device.online || busy !== null;

  return (
    <>
      <Card
        title="System"
        subtitle="Neustart, Konfigurations-Backup und Firmware — alles mit Bestätigung."
      >
        <div className="flex flex-col gap-4">
          {!device.online && (
            <Alert variant="neutral">Router offline — Systemaktionen sind deaktiviert.</Alert>
          )}
          {note && (
            <Alert variant={note.variant} dismissible onDismiss={() => setNote(null)}>
              {note.text}
            </Alert>
          )}

          {busy === "firmware_update" && (
            <FirmwareProgressBar
              progress={flashProgress?.progress ?? null}
              seconds={flashProgress?.seconds ?? null}
            />
          )}

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <ActionCard
              title="Neustart"
              description="Startet den Router neu. Er ist danach einige Minuten nicht erreichbar."
            >
              <Button
                variant="danger-outline"
                iconName="power"
                disabled={disabled}
                onClick={() => setConfirmReboot(true)}
              >
                Reboot
              </Button>
            </ActionCard>

            <ActionCard
              title="Backup"
              description="Erzeugt ein sysupgrade-Archiv der Konfiguration und legt es auf dem Server ab."
            >
              <Button
                variant="outline"
                iconName="download"
                disabled={disabled}
                loading={busy === "backup"}
                onClick={() => void backup()}
              >
                Backup erstellen
              </Button>
            </ActionCard>

            <FirmwareCard
              version={device.firmwareVersion}
              disabled={disabled}
              flashing={busy === "firmware_update"}
              planned={planned}
              scheduleBusy={scheduleBusy}
              onOpen={() => setFirmwareOpen(true)}
              onMove={() => setMoving(true)}
              onCancel={() => void cancelSchedule()}
            />
          </div>
        </div>
      </Card>

      <SystemSettingsCard device={device} />

      <BackupTable key={backupRuns} device={device} />

      <DeleteDeviceCard device={device} />

      <RebootModal
        open={confirmReboot}
        name={device.hostname ?? device.id}
        busy={busy === "reboot"}
        onClose={() => setConfirmReboot(false)}
        onConfirm={() => void reboot()}
      />

      <FirmwareDialog
        open={firmwareOpen}
        onClose={() => setFirmwareOpen(false)}
        onFlash={(f) => void flash(f)}
        onSchedule={(f, runAt) => void planFlash(f, runAt)}
        planned={planned !== null}
        flashing={busy === "firmware_update"}
      />

      {planned && moving && (
        <FlashPlanModal
          label={firmwareLabel({ version: planned.firmwareVersion, tag: planned.firmwareTag })}
          initial={planned.runAt}
          onClose={() => setMoving(false)}
          onConfirm={(runAt) => void moveSchedule(runAt)}
        />
      )}
    </>
  );
}
